import { Button, Col, Form, message, Row, Spin } from "antd";
import React, { useEffect } from "react";
import { InputField, ReactSelectField } from "../../../../../atoms/FormElement";
import { getInputPlaceholderMessage } from "../../../../../libs/form";
import { useForm } from "react-hook-form";
import { PatchBankDetailsPayload, usePatch } from "./hook/usePatchBankDetails";
import { useSelector } from "react-redux";
import { propertyService } from "../../../../../../configs/constants";
import { BankDetailsForm, useInitializeBankDetail } from "./hook/useInitializeBankDetail";
import { useMaskedFields } from "../EditTenant/useMaskedFields";
import { CloseOutlined, EditOutlined } from "@ant-design/icons";

/**
 * @author
 * @function @
 **/

const EditBankDetails = (props) => {
  const { bankDetails, onClose, onSubmit, bankList } = props;
  const { control, handleSubmit, setValue, getValues, reset } = useForm<BankDetailsForm>();

  const { patchData, loading, error } = usePatch<PatchBankDetailsPayload>();
  const maskingFields = ["accountNo", "iban", "accountHolderName", "bankName"];

  const { editingFields, initialize, resetField, setEditMode, preparePayload } = useMaskedFields(maskingFields);

  useEffect(() => {
    initialize([
      { key: "accountNo", value: bankDetails?.accountNo || "" },
      { key: "iban", value: bankDetails?.iban || "" },
      { key: "bankName", value: bankDetails?.bankName || "" },
      { key: "accountHolderName", value: bankDetails?.accountHolderName || "" },
    ]);
  }, [bankDetails]);

  useInitializeBankDetail(setValue, bankDetails, bankList);
  const tenantData = useSelector((state: any) => state.tenantsSummary.tenantData?.userDetail);
  const updateBank = async () => {
    const currentValues = getValues();

    const addIfValid = (obj, key, value) => {
      if (value || value === "") obj[key] = value;
    };

    const bankDetails = {} as BankDetailsForm;

    // ✅ ONLY this (no transform needed anymore)
    const changedData = preparePayload(currentValues) as BankDetailsForm;

    if ("bankName" in changedData && !Array.isArray(changedData.bankName)) {
      addIfValid(bankDetails, "bankName", changedData.bankName);
    }

    addIfValid(bankDetails, "accountHolderName", changedData.accountHolderName);
    addIfValid(bankDetails, "accountNo", changedData.accountNo);
    addIfValid(bankDetails, "iban", changedData.iban);

    // ❗ if nothing valid changed → just close modal
    if (Object.keys(bankDetails).length === 0) {
      onClose();
      return;
    }

    const payload = { bankDetails };

    const url = `${propertyService}/tenant/${tenantData.userId}/bank-details`;
    const result = await patchData(url, payload);

    if (result) {
      onSubmit();
      reset();
      onClose();
      message.success("Bank detail updated successfully");
    }
  };
  return (
    <Form layout="vertical" onFinish={handleSubmit(updateBank)}>
      {loading && !error && (
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "35vh" }}>
              <div>
                <Spin size="large" className="ag-fontSize32" />
              </div>
            </div>
          </Col>
        </Row>
      )}

      {!loading && (
        <>
          <Row gutter={[24, 24]}>
            <Col span={12}>
              <ReactSelectField
                control={control}
                label="BANK NAME"
                selectOption={bankList || []}
                fieldname={`bankName`}
                placeholder={"Bank"}
              />
            </Col>

            <Col span={12}>
              <InputField
                control={control}
                label={"Account Name"}
                fieldname={`accountHolderName`}
                setValue={setValue}
                iProps={{ placeholder: getInputPlaceholderMessage("Account Name") }}
              />
            </Col>

            <Col span={12}>
              <InputField
                control={control}
                label={"Account No"}
                fieldname={`accountNo`}
                setValue={setValue}
                iProps={{
                  placeholder: getInputPlaceholderMessage("Account No"),
                  readOnly: !editingFields["accountNo"],
                  addonBefore: (
                    <span style={{ display: "flex", gap: 6 }}>
                      {/* EDIT ICON */}
                      {!editingFields["accountNo"] ? (
                        <EditOutlined
                          onClick={() => setEditMode("accountNo", setValue)}
                          style={{ cursor: "pointer" }}
                        />
                      ) : (
                        /* CLOSE ICON */
                        <CloseOutlined
                          onClick={() => resetField("accountNo", setValue)}
                          style={{ cursor: "pointer" }}
                        />
                      )}
                    </span>
                  ),
                }}
              />
            </Col>

            <Col span={12}>
              <InputField
                control={control}
                label={"IBAN"}
                fieldname={`iban`}
                setValue={setValue}
                iProps={{
                  placeholder: getInputPlaceholderMessage("IBAN"),
                  readOnly: !editingFields["iban"],
                  addonBefore: (
                    <span style={{ display: "flex", gap: 6 }}>
                      {/* EDIT ICON */}
                      {!editingFields["iban"] ? (
                        <EditOutlined onClick={() => setEditMode("iban", setValue)} style={{ cursor: "pointer" }} />
                      ) : (
                        /* CLOSE ICON */
                        <CloseOutlined onClick={() => resetField("iban", setValue)} style={{ cursor: "pointer" }} />
                      )}
                    </span>
                  ),
                }}
              />
            </Col>
          </Row>
          <Row gutter={24} justify="space-between" style={{ marginTop: "20px" }}>
            <Col>
              <Button
                className="ag-gray-button-outline theme_grey_btn"
                htmlType="button"
                onClick={() => {
                  props.onCancel();
                }}
              >
                Back
              </Button>
            </Col>
            <Col>
              <Button className="ag-green-button theme_btn" htmlType="submit">
                {"Update"}
              </Button>
            </Col>
          </Row>
        </>
      )}
    </Form>
  );
};

export default React.memo(EditBankDetails);
